import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { quranAudio } from '../audio/quranAudio';
import { quranAudioProvider } from '../audio/quranAudioProvider';
import { audioStorage } from '../audio/audioStorage';
import api from '../services/api';

const AudioCtx = createContext(null);

export function AudioProvider({ children }) {
  const [state, setState] = useState(quranAudio.getState());

  // Subscribe to player state
  useEffect(() => {
    const unsub = quranAudio.subscribe(setState);
    return () => unsub();
  }, []);

  // Hydrate user audio prefs once
  useEffect(() => {
    api.get('/auth/me')
      .then((r) => {
        const a = (r.data?.preferences?.audio) || {};
        if (a.preferred_reciter_id) quranAudio.setReciter(a.preferred_reciter_id);
        if (a.preferred_riwaya) quranAudio.setRiwaya(a.preferred_riwaya);
        if (a.audio_mode) quranAudio.setAudioMode(a.audio_mode);
      })
      .catch(() => { /* anonymous user — keep defaults */ });
  }, []);

  const setReciter = useCallback(async (reciterId) => {
    quranAudio.setReciter(reciterId);
    try { await api.patch('/auth/me/audio-preferences', { preferred_reciter_id: reciterId }); }
    catch { /* ignore: anonymous user */ }
  }, []);

  const setAudioMode = useCallback(async (mode) => {
    quranAudio.setAudioMode(mode);
    try { await api.patch('/auth/me/audio-preferences', { audio_mode: mode }); }
    catch { /* ignore */ }
  }, []);

  const value = {
    state,
    reciters: quranAudioProvider.getAvailableReciters(),
    setReciter,
    setAudioMode,
    // Playback shortcuts re-exposed for convenience
    playAyah: (a) => quranAudio.playAyah(a),
    playSurah: (n) => quranAudio.playSurah(n),
    playHizb: (n) => quranAudio.playHizb(n),
    playFullQuran: () => quranAudio.playFullQuran(),
    playMemorizationRange: (s) => quranAudio.playMemorizationRange(s),
    pause: () => quranAudio.pause(),
    resume: () => quranAudio.resume(),
    stop: () => quranAudio.stop(),
    next: () => quranAudio.next(),
    previous: () => quranAudio.previous(),
    seek: (s) => quranAudio.seek(s),
    clearNeedsDownload: () => quranAudio.clearNeedsDownload(),
    storage: audioStorage,
  };

  return <AudioCtx.Provider value={value}>{children}</AudioCtx.Provider>;
}

export function useAudio() {
  const ctx = useContext(AudioCtx);
  if (!ctx) throw new Error('useAudio must be inside <AudioProvider>');
  return ctx;
}
